import dayjs from 'dayjs';
import { ChangeEvent, useState } from 'react';

import { ActionIcon, Alert, Box, Flex, Group, Text } from '@mantine/core';

import { useCreatePersonSettings, useUpdatePersonSettings } from '@/entities/Settings';

import { stringifyObjectValues } from '@/shared/helpers/utils.ts';
import { normalizeWorkTime } from '@/shared/helpers/workTime.ts';
import useAppStore from '@/shared/store/app.store';
import { getSettings, useSettingsStore } from '@/shared/store/settings.store.ts';
import useUserStore from '@/shared/store/user.store.ts';
import { TDateRange } from '@/shared/types/app.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { LabelInput } from '@/shared/uikit/components/input';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';
import classes from '@/shared/uikit/components/picker/RangeTimePicker/Picker.module.css';
import { WheelPicker } from '@/shared/uikit/components/picker/WheelPicker/WheelPicker';

import { IconChevronDown, IconChevronUp, IconInfoCircle } from '@tabler/icons-react';

const hourItems = Array.from({ length: 24 }, (_, index) => ({
	value: index.toString().padStart(2, '0'),
	label: index.toString().padStart(2, '0'),
}));

const minuteItems = Array.from({ length: 4 }, (_, index) => ({
	value: `${(index * 15).toString().padStart(2, '0')}`,
	label: `${(index * 15).toString().padStart(2, '0')}`,
}));

type Props = {
	opened: boolean;
	onClose(): void;
};

export const WorkTimeSelector = ({ opened, onClose }: Props) => {
	const isMobile = useAppStore((state) => state.isMobile);
	const user = useUserStore((state) => state.user);
	const id = useSettingsStore((state) => state.id);
	const workTime = useSettingsStore((state) => state.workTime);
	const setWorkTime = useSettingsStore((state) => state.setWorkTime);
	const [currentSelector, setCurrentSelector] = useState(0);
	const [currentTime, setCurrentTime] = useState<TDateRange>(() => normalizeWorkTime(workTime));

	const { createSettings, isCreatingSettings, isCreatingSettingsError } = useCreatePersonSettings();
	const { updateSettings, isUpdatingSettings, isUpdatingSettingsError } = useUpdatePersonSettings();

	const handleChangeHour = (hour: number) => {
		const newTime = [...currentTime] as TDateRange;

		if (currentSelector === 0) {
			newTime[0] = dayjs(newTime[0]).set('hour', hour).toDate();
		} else {
			newTime[1] = dayjs(newTime[1]).set('hour', hour).toDate();
		}

		return setCurrentTime(newTime);
	};

	const handleChangeMinute = (minute: number) => {
		const newTime = [...currentTime] as TDateRange;

		if (currentSelector === 0) {
			newTime[0] = dayjs(newTime[0]).set('minute', minute).toDate();
		} else {
			newTime[1] = dayjs(newTime[1]).set('minute', minute).toDate();
		}

		setCurrentTime(newTime);
	};

	const handleChangeTime = (
		event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		selector: number,
	) => {
		const rawTime = event.target.value.split(':');
		const newTime = [...currentTime] as TDateRange;
		if (selector === 0) {
			newTime[0] = dayjs(newTime[0])
				.set('hour', Number(rawTime[0]))
				.set('minute', Number(rawTime[1]))
				.toDate();
		} else {
			newTime[1] = dayjs(newTime[1])
				.set('hour', Number(rawTime[0]))
				.set('minute', Number(rawTime[1]))
				.toDate();
		}

		setCurrentTime(newTime);
	};

	const handleSave = () => {
		const settings = getSettings();
		const newSettings: { [key: string]: unknown } = {
			...settings,
			// location: null,
			// currentLocation: null,
			workTime: currentTime,
		};

		stringifyObjectValues(newSettings);

		id
			? updateSettings({ settingsId: String(id), payload: { ...newSettings } })
			: createSettings({ personId: user!.id, payload: { ...newSettings } });

		if (
			!isCreatingSettings ||
			!isCreatingSettingsError ||
			!isUpdatingSettings ||
			!isUpdatingSettingsError
		) {
			setWorkTime(currentTime);
			onClose();
		}
	};

	// const handleSave = () => {
	// 	setWorkTime(currentTime);
	// 	close();
	// };

	const content = (
		<Group justify="center">
			<Alert variant="light" color="gray" icon={<IconInfoCircle />}>
				<Text>Данное время будет использоваться по умолчанию при бронировании.</Text>
			</Alert>
			<Flex align="center" gap={12} w={'100%'}>
				<LabelInput
					label="Начало"
					value={dayjs(currentTime[0]).format('HH:mm')}
					placeholder="Не выбрано"
					onClick={() => setCurrentSelector(0)}
					{...(isMobile
						? { readOnly: true }
						: {
								time: true,
								onChange: (event) => handleChangeTime(event, 0),
							})}
				/>
				<Box>-</Box>
				<LabelInput
					label="Конец"
					value={dayjs(currentTime[1]).format('HH:mm')}
					placeholder="Не выбрано"
					onClick={() => setCurrentSelector(1)}
					{...(isMobile
						? { readOnly: true }
						: {
								time: true,
								onChange: (event) => handleChangeTime(event, 1),
							})}
				/>
			</Flex>
			<Flex align="center" justify="center" gap={44} py={16}>
				<ActionIcon
					color="grey"
					variant="light"
					classNames={classes}
					onClick={() => handleChangeHour(dayjs(currentTime[currentSelector]).hour() - 1)}
				>
					<IconChevronUp />
				</ActionIcon>
				<ActionIcon
					color="grey"
					variant="light"
					classNames={classes}
					onClick={() => handleChangeMinute(dayjs(currentTime[currentSelector]).minute() - 15)}
				>
					<IconChevronUp />
				</ActionIcon>
			</Flex>
			<Flex align="center" justify="center">
				{currentSelector === 0 && (
					<WheelPicker
						hourItems={hourItems}
						hourValue={
							hourItems[hourItems.map((el) => el.value).indexOf(dayjs(currentTime[0]).format('HH'))]
								?.value
						}
						onHourChange={handleChangeHour}
						minuteItems={minuteItems}
						minuteValue={
							minuteItems[
								minuteItems.map((el) => el.value).indexOf(dayjs(currentTime[0]).format('mm'))
							]?.value
						}
						onMinuteChange={handleChangeMinute}
						itemHeight={62}
						containerHeight={300}
					/>
				)}
				{currentSelector === 1 && (
					<WheelPicker
						hourItems={hourItems}
						hourValue={
							hourItems[hourItems.map((el) => el.value).indexOf(dayjs(currentTime[1]).format('HH'))]
								?.value
						}
						onHourChange={handleChangeHour}
						minuteItems={minuteItems}
						minuteValue={
							minuteItems[
								minuteItems.map((el) => el.value).indexOf(dayjs(currentTime[1]).format('mm'))
							]?.value
						}
						onMinuteChange={handleChangeMinute}
						itemHeight={62}
						containerHeight={300}
					/>
				)}
			</Flex>
			<Flex align="center" justify="center" gap={44} py={16}>
				<ActionIcon
					color="grey"
					variant="light"
					classNames={classes}
					onClick={() => handleChangeHour(dayjs(currentTime[currentSelector]).hour() + 1)}
				>
					<IconChevronDown />
				</ActionIcon>

				<ActionIcon
					color="grey"
					variant="light"
					classNames={classes}
					onClick={() => handleChangeMinute(dayjs(currentTime[currentSelector]).minute() + 15)}
				>
					<IconChevronDown />
				</ActionIcon>
			</Flex>
		</Group>
	);

	if (isMobile === null) return;

	if (isMobile)
		return (
			<BottomSheet
				opened={opened}
				onClose={onClose}
				title="Рабочее время"
				withCloseButton={!!onClose}
				withBackButton={false}
				buttons={[
					{
						children: 'Сохранить',
						onClick: handleSave,
						loading: isCreatingSettings || isUpdatingSettings,
					},
				]}
			>
				{content}
			</BottomSheet>
		);

	return (
		<ModalSheet
			opened={opened}
			onClose={onClose}
			title="Рабочее время"
			withCloseButton={!!onClose}
			buttons={[
				{
					children: 'Сохранить',
					onClick: handleSave,
					loading: isCreatingSettings || isUpdatingSettings,
				},
			]}
			centered
		>
			{content}
		</ModalSheet>
	);
};
